/**
 * sun-product-slider.js — Web Component <sun-product-slider>
 *
 * Custom product page image carousel with infinite clones, touch swipe,
 * mouse drag, and pagination segment sync.
 *
 * Scope: rendered inside sections/sun-product.liquid (.sun-product__media).
 * Loaded from that section's <script> tag.
 */
if (!customElements.get('sun-product-slider')) {
  customElements.define(
    'sun-product-slider',
    class SunProductSlider extends HTMLElement {
      constructor() {
        super();
        this.trackIndex = 0;
        this.totalRealSlides = 0;
        this.cloneCount = 2;
        this.snapTimeout = null;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragDelta = 0;
      }

      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.realSlides = Array.from(this.querySelectorAll('[data-slide]'));
        this.totalRealSlides = this.realSlides.length;

        const media = this.closest('.sun-product__media');
        if (!media) return;

        this.prevBtn = media.querySelector('[data-arrow-prev]');
        this.nextBtn = media.querySelector('[data-arrow-next]');
        this.paginationSegments = media.querySelectorAll('[data-pagination-segment]');

        if (this.totalRealSlides <= 1) return;

        this.setupClones();

        this.trackIndex = this.cloneCount + this.#getInitialIndex();
        this.setPosition(false);

        this.onPrev = () => this.prev();
        this.onNext = () => this.next();
        this.prevBtn?.addEventListener('click', this.onPrev);
        this.nextBtn?.addEventListener('click', this.onNext);

        this.onTransitionEnd = this.handleTransitionEnd.bind(this);
        this.track.addEventListener('transitionend', this.onTransitionEnd);

        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchEnd = this.handleTouchEnd.bind(this);
        this.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.addEventListener('touchend', this.onTouchEnd, { passive: true });

        this.onMouseDown = this.handleMouseDown.bind(this);
        this.onMouseMove = this.handleMouseMove.bind(this);
        this.onMouseUp = this.handleMouseUp.bind(this);
        this.addEventListener('mousedown', this.onMouseDown);

        this.onResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.onResize);

        this.onMediaSelect = this.handleMediaSelect.bind(this);
        this.addEventListener('theme:media:select', this.onMediaSelect);

        // Safety net #1: also listen at <product-info> level in case the event
        // is dispatched on an element outside the slider subtree (e.g. grid item).
        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('theme:media:select', this.onMediaSelect);

        // Safety net #2: subscribe to the PubSub `variantChange` event, published by
        // product-info.js after a variant swap completes. Source of truth: the
        // variant JSON itself, independent of any DOM event bubbling.
        if (typeof window.subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) {
          this.variantChangeUnsubscribe = window.subscribe(
            window.theme.PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
        }

        this.updatePagination();
      }

      disconnectedCallback() {
        clearTimeout(this.snapTimeout);
        this.prevBtn?.removeEventListener('click', this.onPrev);
        this.nextBtn?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('transitionend', this.onTransitionEnd);
        this.removeEventListener('touchstart', this.onTouchStart);
        this.removeEventListener('touchend', this.onTouchEnd);
        this.removeEventListener('mousedown', this.onMouseDown);
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
        window.removeEventListener('resize', this.onResize);
        this.removeEventListener('theme:media:select', this.onMediaSelect);
        this.productInfo?.removeEventListener('theme:media:select', this.onMediaSelect);
        this.variantChangeUnsubscribe?.();
      }

      setupClones() {
        const slides = this.realSlides;

        for (let i = this.cloneCount - 1; i >= 0; i--) {
          const idx = (this.totalRealSlides - this.cloneCount + i + this.totalRealSlides) % this.totalRealSlides;
          const clone = slides[idx].cloneNode(true);
          clone.setAttribute('data-clone', '');
          clone.removeAttribute('data-index');
          this.track.insertBefore(clone, this.track.firstChild);
        }

        for (let i = 0; i < this.cloneCount; i++) {
          const clone = slides[i % this.totalRealSlides].cloneNode(true);
          clone.setAttribute('data-clone', '');
          clone.removeAttribute('data-index');
          this.track.appendChild(clone);
        }

        // Les videos clonées via cloneNode(true) conservent leur attribut `autoplay`
        // mais certains navigateurs (Safari, Chrome Android) ne lancent pas la lecture
        // automatiquement sur les clones. On force un `.play()` explicite — muted, donc
        // aucune restriction d'interaction utilisateur.
        this.track.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        });
      }

      get isMobile() {
        return window.innerWidth < 990;
      }

      get slidesPerView() {
        return this.isMobile ? 1 : 2;
      }

      get realIndex() {
        return ((this.trackIndex - this.cloneCount) % this.totalRealSlides + this.totalRealSlides) % this.totalRealSlides;
      }

      next() {
        this.snapIfNeeded();
        this.trackIndex++;
        this.setPosition(true);
        this.scheduleSnap();
      }

      prev() {
        this.snapIfNeeded();
        this.trackIndex--;
        this.setPosition(true);
        this.scheduleSnap();
      }

      goToMediaId(mediaId) {
        if (mediaId === undefined || mediaId === null) return;

        const slide =
          this.querySelector(`[data-media-id="${mediaId}"]:not([data-clone])`) ||
          this.querySelector(`[data-image-id="${mediaId}"]:not([data-clone])`);
        if (!slide) return;

        const index = parseInt(slide.dataset.index, 10);
        if (isNaN(index)) return;

        clearTimeout(this.snapTimeout);
        this.trackIndex = this.cloneCount + index;
        this.setPosition(true);
        this.updatePagination();
      }

      setPosition(animate) {
        if (animate) {
          this.track.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)';
        } else {
          this.track.style.transition = 'none';
          this.track.offsetHeight;
        }

        const percent = (this.trackIndex * 100) / this.slidesPerView;
        this.track.style.transform = `translate3d(-${percent}%, 0, 0)`;

        this.updatePagination();
      }

      snapIfNeeded() {
        clearTimeout(this.snapTimeout);

        if (this.trackIndex >= this.cloneCount + this.totalRealSlides) {
          this.trackIndex -= this.totalRealSlides;
          this.setPosition(false);
        } else if (this.trackIndex < this.cloneCount) {
          this.trackIndex += this.totalRealSlides;
          this.setPosition(false);
        }
      }

      scheduleSnap() {
        clearTimeout(this.snapTimeout);
        this.snapTimeout = setTimeout(() => this.snapIfNeeded(), 550);
      }

      handleTransitionEnd(e) {
        if (e.target !== this.track) return;
        this.snapIfNeeded();
      }

      updatePagination() {
        if (!this.paginationSegments || this.paginationSegments.length === 0) return;

        const realIdx = this.realIndex;
        this.paginationSegments.forEach((segment) => {
          const index = parseInt(segment.dataset.paginationSegment, 10);
          segment.classList.toggle('is-active', index === realIdx);
        });
      }

      handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
      }

      handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        const delta = this.touchStartX - this.touchEndX;
        const threshold = 50;

        if (Math.abs(delta) > threshold) {
          if (delta > 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      }

      handleMouseDown(e) {
        if (e.button !== 0) return;
        e.preventDefault();
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragDelta = 0;
        this.track.style.transition = 'none';
        this.style.cursor = 'grabbing';
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
      }

      handleMouseMove(e) {
        if (!this.isDragging) return;
        this.dragDelta = e.clientX - this.dragStartX;
        const basePercent = (this.trackIndex * 100) / this.slidesPerView;
        const dragPercent = (this.dragDelta / this.offsetWidth) * 100;
        this.track.style.transform = `translate3d(calc(-${basePercent}% + ${dragPercent}%), 0, 0)`;
      }

      handleMouseUp() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.style.cursor = '';
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);

        const threshold = this.offsetWidth * 0.1;
        if (Math.abs(this.dragDelta) > threshold) {
          if (this.dragDelta < 0) {
            this.next();
          } else {
            this.prev();
          }
        } else {
          this.setPosition(true);
        }
      }

      handleResize() {
        this.setPosition(false);
      }

      handleMediaSelect(event) {
        const mediaId = event.detail?.id;
        if (mediaId === undefined || mediaId === null) return;
        this.goToMediaId(mediaId);
      }

      handleVariantChange(payload) {
        // Only react to variant changes for our own section (avoid cross-section noise).
        const sectionId = this.productInfo?.dataset.section;
        if (sectionId && payload?.data?.sectionId && payload.data.sectionId !== sectionId) return;

        const mediaId = payload?.data?.variant?.featured_media?.id;
        if (mediaId === undefined || mediaId === null) return;
        this.goToMediaId(mediaId);
      }

      #getInitialIndex() {
        const initialMediaId = this.getAttribute('data-initial-media-id');
        if (!initialMediaId) return 0;

        const slide = this.realSlides.find((s) => s.getAttribute('data-image-id') === initialMediaId);
        if (!slide) return 0;

        const index = parseInt(slide.dataset.index, 10);
        return isNaN(index) ? 0 : index;
      }
    }
  );
}
